import { Button } from '@/components';
import { env } from '@/env';
import { useAuth } from '@/features/auth/AuthProvider';
import { MachineCard } from '@/features/runner-monitor/MachineCard';
import { AddMachineModal } from '@/features/runner-monitor/MachineTokenModal';
import { FilterSelect, QueryStates, RunnerPage } from '@/features/runner-monitor/RunnerPage';
import { useRunnerMachines, useRunners } from '@/features/runner-monitor/hooks';
import { useSearchParams } from '@solidjs/router';
import { For, Show, createSignal } from 'solid-js';

export default function RunnersMachines() {
  const { session } = useAuth();
  const canManage = () => session()?.permissions.has('runners:machines:manage') ?? false;
  const machines = useRunnerMachines();
  const runners = useRunners();
  const [params, setParams] = useSearchParams<{ os?: string; status?: string }>();
  const [adding, setAdding] = createSignal(false);

  const visible = () =>
    (machines.data?.data ?? []).filter(
      (m) =>
        (!params.os || m.os === params.os) &&
        (!params.status || (params.status === 'online') === m.online),
    );
  const runnerCount = (id: string) =>
    (runners.data?.data ?? []).filter((r) => r.machineId === id).length;

  return (
    <RunnerPage
      title="Machines"
      sub="Machine-level resources, shared by every runner on the machine"
      updatedAt={machines.dataUpdatedAt}
      actions={
        <Show when={canManage()}>
          <Button onClick={() => setAdding(true)}>Add machine</Button>
        </Show>
      }
    >
      <div class="flex flex-wrap gap-3">
        <FilterSelect
          label="OS"
          value={params.os}
          options={[
            { value: 'macos', label: 'macOS' },
            { value: 'windows', label: 'Windows' },
          ]}
          onChange={(os) => setParams({ os })}
        />
        <FilterSelect
          label="Status"
          value={params.status}
          options={[
            { value: 'online', label: 'Online' },
            { value: 'offline', label: 'Offline' },
          ]}
          onChange={(status) => setParams({ status })}
        />
      </div>
      <QueryStates
        pending={machines.isPending}
        error={machines.error}
        empty={visible().length === 0}
        emptyTitle={(machines.data?.data.length ?? 0) > 0 ? 'No machines match' : 'No machines yet'}
        emptyBody="Add a machine, then install the agent on it (apps/agent/README.md)."
      >
        <div class="grid gap-4 lg:grid-cols-2">
          <For each={visible()}>
            {(m) => <MachineCard machine={m} runnerCount={runnerCount(m.id)} />}
          </For>
        </div>
      </QueryStates>
      <AddMachineModal
        open={adding()}
        onClose={() => setAdding(false)}
        apiUrl={env.VITE_API_BASE_URL}
      />
    </RunnerPage>
  );
}
